import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import config from "../Config/config";

const ACCOUNT_MANAGERS = [
  { id: 1, name: "Bert Leffel", phoneNumber: "(317) 523-9070", email: "bert@listernaut.com" },
  { id: 2, name: "Jeff Kaplan", phoneNumber: "(317) 798-4459", email: "jeff@listernaut.com" },
  { id: 3, name: "Mark Seidman", phoneNumber: "(317) 605-7539", email: "mark@listernaut.com" },
  { id: 4, name: "Logan Weaver", phoneNumber: "(317) 500-1460", email: "logan@listernaut.com" },
  { id: 5, name: "Vrushab Hanumesh", phoneNumber: "(812) 671-5565", email: "vrushab1998@gmail.com" },
];

const DEFAULT_MANAGER = ACCOUNT_MANAGERS[0];

const formatDateTime = (millis) => {
  const value = Number(millis);
  if (!Number.isFinite(value) || value <= 0) return "Unknown";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Unknown";
  return d.toLocaleString();
};

const normalizeManager = (manager) => {
  if (!manager) return DEFAULT_MANAGER;
  const byId = ACCOUNT_MANAGERS.find((item) => String(item.id) === String(manager.id));
  if (byId) return byId;
  const byEmail = ACCOUNT_MANAGERS.find(
    (item) => String(item.email || "").toLowerCase() === String(manager.email || "").toLowerCase()
  );
  if (byEmail) return byEmail;
  return DEFAULT_MANAGER;
};

const SupportTicketsCenter = () => {
  const location = useLocation();
  const userId = String(localStorage.getItem("userId") || "").trim();
  const [supportAdminMode, setSupportAdminMode] = useState(
    String(localStorage.getItem("admin") || "").toLowerCase() === "true"
  );
  const [loading, setLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [ticketActionLoading, setTicketActionLoading] = useState(false);
  const [managerFilter, setManagerFilter] = useState("all");
  const [showLinkedChat, setShowLinkedChat] = useState(false);

  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });

  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    phone: "",
    problem: "",
    screenshot: null,
  });
  const preferredTicketIdFromUrl =
    String(new URLSearchParams(location.search).get("ticketId") || "").trim();
  const adminMode = supportAdminMode;

  const fetchUserProfile = async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`${config.API_BASE_URL}/get-user-details`, {
        params: { userId },
      });
      const user = response.data || {};
      const permissions = user.permissions || {};
      const isAdmin =
        permissions.superAdmin === true ||
        permissions.manageRequests === true ||
        String(localStorage.getItem("admin") || "").toLowerCase() === "true";
      setSupportAdminMode(isAdmin);

      const nextProfile = {
        name: String(user.username || "").trim(),
        email: String(user.email || "").trim(),
        phone: String(user.phone || "").trim(),
      };
      setProfile(nextProfile);
      setCreateForm((prev) => ({
        ...prev,
        name: prev.name || nextProfile.name,
        email: prev.email || nextProfile.email,
        phone: prev.phone || nextProfile.phone,
      }));
    } catch {
      // ignore
    }
  };

  const fetchTickets = async (preferredTicketId = "") => {
    if (!adminMode && !userId) {
      setTicketsError("Unable to identify your user account. Please log in again.");
      setTickets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setTicketsError("");
    try {
      const response = await axios.get(`${config.API_BASE_URL}/docs/support-tickets`, {
        params: {
          scope: adminMode ? "admin" : "user",
          userId,
          limit: 150,
        },
      });
      const list = Array.isArray(response.data?.tickets) ? response.data.tickets : [];
      setTickets(list);

      if (preferredTicketId && list.some((item) => item.ticketId === preferredTicketId)) {
        setSelectedTicketId(preferredTicketId);
      } else if (selectedTicketId && list.some((item) => item.ticketId === selectedTicketId)) {
        setSelectedTicketId(selectedTicketId);
      } else {
        setSelectedTicketId(list[0]?.ticketId || "");
      }
    } catch (err) {
      setTicketsError(err.response?.data?.error || "Failed to load support tickets.");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedTicket = async () => {
    if (!selectedTicketId) {
      setSelectedTicket(null);
      setMessages([]);
      return;
    }

    setDetailsLoading(true);
    setDetailsError("");
    try {
      const [detailResponse, messagesResponse] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/docs/support-tickets/${encodeURIComponent(selectedTicketId)}`),
        axios.get(
          `${config.API_BASE_URL}/docs/support-tickets/${encodeURIComponent(selectedTicketId)}/messages`,
          { params: { limit: 400 } }
        ),
      ]);

      setSelectedTicket(detailResponse.data || null);
      const nextMessages = Array.isArray(messagesResponse.data?.messages)
        ? messagesResponse.data.messages
        : [];
      setMessages(nextMessages);
    } catch (err) {
      setDetailsError(err.response?.data?.error || "Failed to load ticket details.");
      setSelectedTicket(null);
      setMessages([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetchTickets(preferredTicketIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, userId, preferredTicketIdFromUrl]);

  useEffect(() => {
    fetchSelectedTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId]);

  useEffect(() => {
    setShowLinkedChat(false);
  }, [selectedTicketId]);

  const sendMessage = async () => {
    const content = String(messageInput || "").trim();
    if (!content || !selectedTicketId || sendingMessage) return;

    setSendingMessage(true);
    try {
      await axios.post(
        `${config.API_BASE_URL}/docs/support-tickets/${encodeURIComponent(selectedTicketId)}/messages`,
        {
          userId,
          role: adminMode ? "admin" : "user",
          content,
          senderName: profile.name || (adminMode ? "Admin" : "User"),
          senderEmail: profile.email || "",
        }
      );
      setMessageInput("");
      await fetchSelectedTicket();
      await fetchTickets(selectedTicketId);
    } catch (err) {
      setDetailsError(err.response?.data?.error || "Failed to send message.");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSupportMessageKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    if (sendingMessage || !String(messageInput || "").trim()) return;
    sendMessage();
  };

  const assignManager = async (managerId) => {
    if (!selectedTicketId || assigning) return;
    setAssigning(true);
    setDetailsError("");
    try {
      const response = await axios.patch(
        `${config.API_BASE_URL}/docs/support-tickets/${encodeURIComponent(selectedTicketId)}/assign`,
        { managerId, userId }
      );
      if (response.data?.warning) {
        setDetailsError(`Assignment updated, but email notification failed: ${response.data.warning}`);
      }
      await fetchSelectedTicket();
      await fetchTickets(selectedTicketId);
    } catch (err) {
      setDetailsError(err.response?.data?.error || "Failed to assign manager.");
    } finally {
      setAssigning(false);
    }
  };

  const updateTicketStatus = async (status) => {
    if (!selectedTicketId || ticketActionLoading) return;
    setTicketActionLoading(true);
    setDetailsError("");
    try {
      await axios.patch(
        `${config.API_BASE_URL}/docs/support-tickets/${encodeURIComponent(selectedTicketId)}/status`,
        { userId, status }
      );
      await fetchSelectedTicket();
      await fetchTickets(selectedTicketId);
    } catch (err) {
      setDetailsError(err.response?.data?.error || "Failed to update ticket status.");
    } finally {
      setTicketActionLoading(false);
    }
  };

  const deleteTicket = async () => {
    if (!selectedTicketId || ticketActionLoading) return;
    const confirmed = window.confirm(
      "Delete this support ticket permanently? This also deletes all ticket messages."
    );
    if (!confirmed) return;

    setTicketActionLoading(true);
    setDetailsError("");
    try {
      await axios.delete(
        `${config.API_BASE_URL}/docs/support-tickets/${encodeURIComponent(selectedTicketId)}`,
        { data: { userId } }
      );

      setSelectedTicket(null);
      setMessages([]);
      setSelectedTicketId("");
      await fetchTickets();
    } catch (err) {
      setDetailsError(err.response?.data?.error || "Failed to delete support ticket.");
    } finally {
      setTicketActionLoading(false);
    }
  };

  const createTicket = async () => {
    if (createSubmitting) return;
    setCreateError("");

    const name = String(createForm.name || "").trim();
    const email = String(createForm.email || "").trim();
    const phone = String(createForm.phone || "").trim();
    const problem = String(createForm.problem || "").trim();

    if (!userId) {
      setCreateError("Unable to identify your account. Please log in again.");
      return;
    }

    if (!name || !email || !phone || !problem) {
      setCreateError("Name, email, phone, and problem are required.");
      return;
    }

    setCreateSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("problem", problem);
      formData.append("source", "support-center");

      if (createForm.screenshot) {
        formData.append("screenshot", createForm.screenshot);
      }

      const response = await axios.post(`${config.API_BASE_URL}/docs/support-tickets`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newTicketId = response.data?.ticketId || "";
      setCreateOpen(false);
      setCreateForm((prev) => ({
        ...prev,
        problem: "",
        screenshot: null,
      }));

      await fetchTickets(newTicketId);
    } catch (err) {
      setCreateError(err.response?.data?.error || "Failed to create support ticket.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const title = adminMode ? "Support Tickets (Admin)" : "Support Tickets";
  const subtitle = adminMode
    ? "View all tickets, assign account managers, and reply to users."
    : "Create tickets, track status, and chat with support.";
  const filteredTickets = adminMode
    ? tickets.filter((ticket) => {
        if (managerFilter === "all") return true;
        const manager = normalizeManager(ticket.assignedManager);
        return String(manager.id) === String(managerFilter);
      })
    : tickets;
  const linkedChatSnapshot = Array.isArray(selectedTicket?.chatSnapshot)
    ? [...selectedTicket.chatSnapshot].sort(
        (a, b) => Number(a?.createdAtMs || 0) - Number(b?.createdAtMs || 0)
      )
    : [];

  return (
    <div className="p-3 sm:p-5">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-gradient-to-r from-[#1f3a8a] to-[#3f51b5] text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold">{title}</h1>
            <p className="text-[12px] text-indigo-100">{subtitle}</p>
            <p className="text-[11px] text-indigo-100/95 mt-1 max-w-[860px]">
              If you haven&apos;t received our email in your inbox, please check your spam or junk folder and move{" "}
              <span className="font-semibold">jeff@auctionfuel.com</span> to your inbox for updates.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
            <button
              type="button"
              onClick={() => fetchTickets(selectedTicketId)}
              className="rounded-lg border border-white/30 bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold"
            >
              Refresh
            </button>
            {adminMode && (
              <select
                value={managerFilter}
                onChange={(event) => setManagerFilter(event.target.value)}
                className="max-w-[160px] sm:max-w-none rounded-lg border border-white/40 bg-white/15 hover:bg-white/20 px-2.5 py-1.5 text-xs font-semibold text-white focus:outline-none"
              >
                <option value="all" className="text-slate-900">
                  All Managers
                </option>
                {ACCOUNT_MANAGERS.map((manager) => (
                  <option key={manager.id} value={String(manager.id)} className="text-slate-900">
                    {manager.name}
                  </option>
                ))}
              </select>
            )}
            {!adminMode && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="rounded-lg border border-emerald-300 bg-emerald-500/30 hover:bg-emerald-500/45 px-3 py-1.5 text-xs font-semibold"
              >
                New Ticket
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-[70vh]">
          <div className="border-r border-slate-200 bg-slate-50/70">
            {loading ? (
              <div className="p-4 text-sm text-slate-500">Loading tickets...</div>
            ) : ticketsError ? (
              <div className="p-4 text-sm text-rose-600">{ticketsError}</div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">
                {adminMode && managerFilter !== "all"
                  ? "No support tickets for the selected manager."
                  : "No support tickets found."}
              </div>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto p-2 space-y-2">
                {filteredTickets.map((ticket) => {
                  const selected = ticket.ticketId === selectedTicketId;
                  const manager = normalizeManager(ticket.assignedManager);

                  return (
                    <button
                      type="button"
                      key={ticket.ticketId}
                      onClick={() => setSelectedTicketId(ticket.ticketId)}
                      className={`w-full text-left rounded-xl border px-3 py-2.5 transition ${
                        selected
                          ? "border-indigo-400 bg-indigo-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[12px] font-semibold text-slate-900 break-all">#{ticket.ticketId}</p>
                        <span className="text-[10px] uppercase tracking-wide text-slate-500 shrink-0">
                          {ticket.status || "open"}
                        </span>
                      </div>
                      <p className="mt-1 text-[12px] text-slate-700 line-clamp-2">
                        {ticket.problem || ticket.lastMessageText || "No details"}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-500 break-all line-clamp-2">
                        {adminMode ? `${ticket.name} • ${ticket.email}` : `Assigned: ${manager.name}`}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {formatDateTime(ticket.lastMessageAtMs || ticket.createdAtMs)}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-3 sm:p-4 bg-white">
            {detailsLoading ? (
              <div className="text-sm text-slate-500">Loading ticket details...</div>
            ) : detailsError ? (
              <div className="text-sm text-rose-600">{detailsError}</div>
            ) : !selectedTicket ? (
              <div className="text-sm text-slate-500">Select a ticket to view details.</div>
            ) : (
              <>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-slate-900 break-all">Ticket #{selectedTicket.ticketId}</p>
                      <p className="text-[11px] text-slate-600 mt-0.5 break-all">
                        {selectedTicket.name} • {selectedTicket.email} • {selectedTicket.phone}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Created {formatDateTime(selectedTicket.createdAtMs)}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Status:{" "}
                        <span className="font-semibold uppercase tracking-wide">
                          {String(selectedTicket.status || "open")}
                        </span>
                      </p>
                    </div>

                    <div className="w-full sm:w-auto sm:min-w-[220px]">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Assigned Manager</p>
                      {adminMode ? (
                        <select
                          disabled={assigning}
                          value={String(normalizeManager(selectedTicket.assignedManager).id)}
                          onChange={(event) => assignManager(event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        >
                          {ACCOUNT_MANAGERS.map((manager) => (
                            <option key={manager.id} value={String(manager.id)}>
                              {manager.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="mt-1 text-sm text-slate-700">
                          {normalizeManager(selectedTicket.assignedManager).name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={ticketActionLoading}
                      onClick={() =>
                        updateTicketStatus(
                          String(selectedTicket.status || "").toLowerCase() === "closed"
                            ? "open"
                            : "closed"
                        )
                      }
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                        ticketActionLoading
                          ? "bg-slate-200 text-slate-500"
                          : String(selectedTicket.status || "").toLowerCase() === "closed"
                          ? "bg-emerald-600 text-white hover:bg-emerald-500"
                          : "bg-amber-600 text-white hover:bg-amber-500"
                      }`}
                    >
                      {ticketActionLoading
                        ? "Updating..."
                        : String(selectedTicket.status || "").toLowerCase() === "closed"
                        ? "Reopen Ticket"
                        : "Close Ticket"}
                    </button>
                    <button
                      type="button"
                      disabled={ticketActionLoading}
                      onClick={deleteTicket}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                        ticketActionLoading
                          ? "bg-slate-200 text-slate-500"
                          : "bg-rose-600 text-white hover:bg-rose-500"
                      }`}
                    >
                      {ticketActionLoading ? "Working..." : "Delete Ticket"}
                    </button>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-slate-700 whitespace-pre-wrap break-words">
                    {selectedTicket.problem || "No problem statement."}
                  </p>

                  {selectedTicket.screenshotUrl && (
                    <div className="mt-2">
                      <a
                        href={selectedTicket.screenshotUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-700 underline underline-offset-2 break-all"
                      >
                        Open screenshot
                      </a>
                    </div>
                  )}

                  {linkedChatSnapshot.length > 0 && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
                      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
                        <div>
                          <p className="text-[12px] font-semibold text-slate-800">Linked AI Chat</p>
                          <p className="text-[11px] text-slate-500">
                            {linkedChatSnapshot.length} message{linkedChatSnapshot.length === 1 ? "" : "s"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowLinkedChat((prev) => !prev)}
                          className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          {showLinkedChat ? "Hide Chat" : "Show Chat"}
                        </button>
                      </div>

                      {showLinkedChat && (
                        <div className="max-h-[32vh] overflow-y-auto p-3 space-y-2 bg-slate-50/70">
                          {linkedChatSnapshot.map((message, index) => {
                            const role = message.role === "assistant" ? "assistant" : "user";
                            const isUser = role === "user";
                            return (
                              <div
                                key={`${message.id || message.createdAtMs || index}-${index}`}
                                className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                              >
                                <div
                                  className={`max-w-[92%] rounded-2xl px-3 py-2 ${
                                    isUser
                                      ? "bg-slate-100 border border-slate-200 text-slate-800"
                                      : "bg-indigo-600 text-white"
                                  }`}
                                >
                                  <p className="text-[13.5px] leading-6 whitespace-pre-wrap break-words">
                                    {String(message.content || "")}
                                  </p>
                                  <p
                                    className={`text-[10px] mt-1 ${
                                      isUser ? "text-slate-500" : "text-indigo-100"
                                    }`}
                                  >
                                    {isUser ? "User" : "Assistant"} • {formatDateTime(message.createdAtMs)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
                    <p className="text-[12px] font-semibold text-slate-800">Support Chat</p>
                  </div>

                  <div className="max-h-[46vh] overflow-y-auto p-3 space-y-2 bg-white">
                    {messages.length === 0 ? (
                      <p className="text-sm text-slate-500">No messages yet.</p>
                    ) : (
                      messages.map((message) => {
                        const role = message.role === "admin" ? "admin" : message.role === "system" ? "system" : "user";
                        const isMine = adminMode ? role === "admin" : role === "user";
                        const isSystem = role === "system";

                        return (
                          <div key={message.id} className={`flex ${isSystem ? "justify-center" : isMine ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[92%] rounded-2xl px-3 py-2 ${
                                isSystem
                                  ? "bg-amber-50 border border-amber-200 text-amber-900"
                                  : isMine
                                  ? "bg-indigo-600 text-white"
                                  : "bg-slate-100 border border-slate-200 text-slate-800"
                              }`}
                            >
                              <p className="text-[13.5px] leading-6 whitespace-pre-wrap break-words">{message.content}</p>
                              <p className={`text-[10px] mt-1 ${isSystem ? "text-amber-700" : isMine ? "text-indigo-100" : "text-slate-500"}`}>
                                {message.senderName ||
                                  (isMine
                                    ? "You"
                                    : role === "admin"
                                    ? "Admin"
                                    : role === "system"
                                    ? "System"
                                    : "User")}{" "}
                                • {formatDateTime(message.createdAtMs)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="p-3 border-t border-slate-200 bg-slate-50">
                    <textarea
                      rows={3}
                      value={messageInput}
                      onChange={(event) => setMessageInput(event.target.value)}
                      onKeyDown={handleSupportMessageKeyDown}
                      placeholder={adminMode ? "Reply to user..." : "Message support..."}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        disabled={sendingMessage || !String(messageInput || "").trim()}
                        onClick={sendMessage}
                        className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                          sendingMessage || !String(messageInput || "").trim()
                            ? "bg-slate-200 text-slate-500"
                            : "bg-indigo-600 text-white hover:bg-indigo-500"
                        }`}
                      >
                        {sendingMessage ? "Sending..." : "Send"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-[1300] bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center px-4">
          <div className="w-full max-w-[560px] rounded-2xl bg-white border border-slate-200 shadow-[0_24px_60px_rgba(15,23,42,0.32)] overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#1f3a8a] to-[#3f51b5] text-white flex items-center justify-between">
              <h3 className="text-sm font-semibold tracking-wide">Create Support Ticket</h3>
              <button
                type="button"
                onClick={() => {
                  if (!createSubmitting) {
                    setCreateOpen(false);
                    setCreateError("");
                  }
                }}
                className="text-white/90 hover:text-white text-lg leading-none"
                disabled={createSubmitting}
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, phone: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Problem</label>
                <textarea
                  rows={5}
                  value={createForm.problem}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, problem: event.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Screenshot (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    setCreateForm((prev) => ({ ...prev, screenshot: event.target.files?.[0] || null }))
                  }
                  className="block w-full text-xs text-slate-600"
                />
              </div>

              {createError && <p className="text-[12px] text-rose-600">{createError}</p>}
            </div>

            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!createSubmitting) {
                    setCreateOpen(false);
                    setCreateError("");
                  }
                }}
                disabled={createSubmitting}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createTicket}
                disabled={createSubmitting}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold text-white ${
                  createSubmitting ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-500"
                }`}
              >
                {createSubmitting ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsCenter;
